'use client';

import { Spinner, Text } from '@lmn/design-system';
import styled from 'styled-components';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import { formatNumber } from '@/utils/numberUtils.ts';
import { getTARatingInterval, toTADisplayScore } from './tripadvisor.helper';
import TripAdvisorScore from './TripAdvisorScore';
import { useFetchHotelOpinion } from './useFetchHotelOpinion';

const Container = styled.div``;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
`;

const ScoreInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  min-height: 40px;

  @media (max-width: 767px) {
    min-height: 80px;
  }
`;

// Category rows mirror the TrustYou popover layout: name · 100px bar · score on one line.
const CategoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Category = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CategoryProgressBar = styled.div<{ $score: number }>`
  background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundDisabled};
  border-radius: 4px;
  height: 8px;
  margin-left: auto;
  position: relative;
  width: 100px;

  @media (max-width: 767px) {
    width: 125px;
  }

  &::after {
    background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundSuccessContrast};
    border-radius: 4px;
    content: '';
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: ${({ $score }) => $score}%;
  }
`;

const ViewMoreButton = styled.button`
  margin-top: 8px;
  padding: 0;
  cursor: pointer;
  background: none;
  border: none;
  color: ${({ theme }) => theme.kallisto.color.text.interactive.textPrimaryDefault};
  ${({ theme }) => theme.kallisto.mixins.font('BodySmall')};
  text-decoration: underline;

  &:hover {
    color: ${({ theme }) => theme.kallisto.color.text.interactive.textPrimaryHover};
  }
`;

type TripAdvisorRatingsPopoverProps = {
  rawScore: number;
  reviewsNum: number;
  /** Internal hotel id — enables the lazy category-breakdown fetch */
  hotelId?: number;
  onViewMore?: () => void;
};

export default function TripAdvisorRatingsPopover({
  rawScore,
  reviewsNum,
  hotelId,
  onViewMore,
}: TripAdvisorRatingsPopoverProps) {
  const { localization } = usePageWrapperData();
  const { t } = useTranslation();
  const { locale } = localization;
  const { categories, isLoading: isLoadingCategories, isError } = useFetchHotelOpinion(hotelId);
  const hasNoData = !rawScore || isError || categories.length === 0;

  if (isLoadingCategories) {
    return (
      <LoadingContainer>
        <Spinner size={20} variant="accent" />
        <Text variant="BodySmall" color="textPrimary">
          {t('vacation.oss.components.searchfilters.main.loading')}
        </Text>
      </LoadingContainer>
    );
  }

  if (hasNoData) {
    return (
      <LoadingContainer>
        <Text variant="Body" color="textPrimary">
          {t('vacation.oss.cards.reviews.not_available')}
        </Text>
      </LoadingContainer>
    );
  }

  const ratingInterval = getTARatingInterval(rawScore);
  const reviewCountFormatted = formatNumber({ locale, number: reviewsNum });

  return (
    <Container>
      <Header>
        <TripAdvisorScore rawScore={rawScore} size="large" />
        <ScoreInfo>
          {ratingInterval && <Text variant="Title5">{t(ratingInterval.translationKey)}</Text>}
          <Text variant="BodySmall" color="textPrimary">
            {reviewCountFormatted} {t('vacation.oss.components.searchresults.tour_tile.reviews')}
          </Text>
        </ScoreInfo>
      </Header>

      <CategoryList>
        {categories.map((category) => (
          <Category key={category.name}>
            <Text variant="BodySmall" color="textPrimary" css="flex: 1 1 0; min-width: 0;">
              {category.name}
            </Text>
            <CategoryProgressBar $score={category.score} />
            <Text variant="BodySmall" color="textPrimary" css="flex-shrink: 0; min-width: 24px; text-align: right;">
              {toTADisplayScore(category.score)}
            </Text>
          </Category>
        ))}
      </CategoryList>

      {onViewMore && (
        <ViewMoreButton type="button" onClick={onViewMore}>
          {t('vacation.select.details.review.viewmore')}
        </ViewMoreButton>
      )}
    </Container>
  );
}
